import { cn } from '@ui-kit/utils';
import { OTPInput, REGEXP_ONLY_DIGITS, type SlotProps } from 'input-otp';
import { type ComponentProps, type FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const LENGTH = 6;

type Props = Omit<ComponentProps<'form'>, 'onSubmit'> & {
  isInvalid: boolean;
  isDisabled: boolean;
  onSubmit?: (value: string, reset: () => void) => void;
  onCodeChange?: (code: string) => void;
  errorMessage?: string;
  infoMessage?: string;
};

type OTPSlotProps = SlotProps & {
  isInvalid: boolean;
  isDisabled: boolean;
};

const OTPSlot = ({ char, isActive, hasFakeCaret, isInvalid, isDisabled }: OTPSlotProps) => {
  const showFocusRing = isActive && !isDisabled;

  return (
    <div
      aria-hidden
      data-otp-slot
      className={cn(
        'border-border-default relative flex h-full min-w-0 flex-1 items-center justify-center rounded-xl border text-center',
        isInvalid && !showFocusRing && 'border-border-error',
        isDisabled && 'text-text-disabled border-border-disabled',
        showFocusRing && 'border-border-dark shadow-[inset_0_0_0_2px_var(--theme-color-border-dark)]',
      )}
    >
      {char}
      {hasFakeCaret ? (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div
            data-otp-caret
            className="bg-text-default motion-safe:animate-otp-caret-blink h-[1.1em] w-0.5"
          />
        </div>
      ) : null}
    </div>
  );
};

const OTPForm = ({
  isInvalid,
  isDisabled,
  onSubmit,
  onCodeChange,
  errorMessage,
  infoMessage,
  className,
  ...formProps
}: Props) => {
  const { t } = useTranslation('Common');
  const [code, setCode] = useState('');
  const shouldFocusRef = useRef(true);
  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(
    function focusOnMountAndReset() {
      // Avoid race condition where input is disabled when trying to focus
      if (shouldFocusRef.current && !isDisabled) {
        shouldFocusRef.current = false;
        inputRef.current?.focus({ preventScroll: true });
      }
    },
    [isDisabled, code],
  );

  const handleReset = useCallback(() => {
    shouldFocusRef.current = true;
    setCode('');
  }, []);

  useEffect(() => {
    if (!onCodeChange) {
      return;
    }

    onCodeChange(code);
  }, [code, onCodeChange]);

  const handleFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Read from the input, since `code` state can be stale when submitted from `onComplete`
    const value = inputRef.current?.value ?? '';
    if (value.length !== LENGTH) {
      return;
    }

    // Blur before parent disables the field so iOS does not paint a native
    // caret on the full-width overlay while the keyboard dismisses.
    inputRef.current?.blur();
    onSubmit?.(value, handleReset);
  };

  return (
    <form
      data-private
      {...formProps}
      ref={formRef}
      className={cn('flex w-full flex-col gap-4', className)}
      onSubmit={handleFormSubmit}
    >
      <OTPInput
        ref={inputRef}
        name="otp-input"
        maxLength={LENGTH}
        value={code}
        onChange={setCode}
        onComplete={() => formRef.current?.requestSubmit()}
        pattern={REGEXP_ONLY_DIGITS}
        enterKeyHint="go"
        autoCorrect="off"
        autoCapitalize="off"
        disabled={isDisabled}
        aria-invalid={isInvalid}
        aria-label={t('otpInputLabel')}
        containerClassName="flex h-12 w-full gap-3 lg:h-[54px] lg:gap-10"
        render={({ slots }) => (
          <>
            {slots.map((slot, index) => (
              <OTPSlot key={index} {...slot} isInvalid={isInvalid} isDisabled={isDisabled} />
            ))}
          </>
        )}
      />
      {infoMessage && !errorMessage ? (
        <p className="text-caption text-text-success text-center">{infoMessage}</p>
      ) : null}
      {errorMessage ? (
        <p role="alert" className="text-caption text-text-error text-center">
          {errorMessage}
        </p>
      ) : null}
    </form>
  );
};

export default OTPForm;
